import { atom } from "jotai";
import { atomFamily, atomWithStorage, RESET } from "jotai/utils";
import type { SetStateAction } from "react";
import { getLocalKey } from "./StorageKeys";

export type VoyageScope = {
  tcCode?: string;
  voyageNo?: string;
};

const sameScope = (a: VoyageScope, b: VoyageScope) => a.tcCode === b.tcCode && a.voyageNo === b.voyageNo;

type FlagName = "continueClicked" | "submitEnabled";

const getFlagStorageKey = (flag: FlagName, scope: VoyageScope) =>
  scope.tcCode !== undefined || scope.voyageNo !== undefined
    ? getLocalKey("flag", flag, scope.tcCode, scope.voyageNo)
    : flag;

const createStoredFlagAtom = (scope: VoyageScope, flag: FlagName, defaultValue = false) => {
  const persistedFlagAtom = atomWithStorage<boolean>(getFlagStorageKey(flag, scope), defaultValue, undefined, {
    getOnInit: true,
  });

  return atom(
    (get) => get(persistedFlagAtom),
    (get, set, update: SetStateAction<boolean>) => {
      const prev = get(persistedFlagAtom);
      const next = typeof update === "function" ? (update as (p: boolean) => boolean)(prev) : update;

      if (next === defaultValue) {
        set(persistedFlagAtom, RESET);
        return;
      }

      set(persistedFlagAtom, next);
    },
  );
};

export const continueClickedAtomFamily = atomFamily(
  (scope: VoyageScope) => createStoredFlagAtom(scope, "continueClicked", false),
  sameScope,
);

export const submitEnabledAtomFamily = atomFamily(
  (scope: VoyageScope) => createStoredFlagAtom(scope, "submitEnabled", false),
  sameScope,
);

// Voyage-scoped non-persistent UI flags used by VoyagePnlPage flow.
export const formSubmittedAtomFamily = atomFamily((scope: VoyageScope) => atom<boolean>(false), sameScope);

export const assesmentCompletedAtomFamily = atomFamily((scope: VoyageScope) => atom<boolean>(false), sameScope);

export const showAlertAtomFamily = atomFamily((scope: VoyageScope) => atom<boolean>(false), sameScope);

export const driversSequenceAtomFamily = atomFamily((scope: VoyageScope) => atom<string[]>([]), sameScope);

export const enabledFlagsAtomFamily = atomFamily(
  (scope: VoyageScope) => atom((get) => get(driversSequenceAtomFamily(scope)).length),
  sameScope,
);
